import { DateTime } from "luxon";

export enum MaterialUnit {
  Kg = 0,
  Each = 1,
  Metre = 2,
  Litre = 3,
}

export const UNIT_LABELS: Record<MaterialUnit, string> = {
  [MaterialUnit.Kg]: "kg",
  [MaterialUnit.Each]: "each",
  [MaterialUnit.Metre]: "m",
  [MaterialUnit.Litre]: "L",
};

export enum StorageClass {
  Detonator = 0,
  HighExplosive = 1,
  BlastingAgent = 2,
  Oxidiser = 3,
  NonHazardous = 4,
  Stemming = 5,
}

export const STORAGE_CLASS_LABELS: Record<StorageClass, string> = {
  [StorageClass.Detonator]: "Detonator",
  [StorageClass.HighExplosive]: "High explosive",
  [StorageClass.BlastingAgent]: "Blasting agent",
  [StorageClass.Oxidiser]: "Oxidiser",
  [StorageClass.NonHazardous]: "Non-hazardous",
  [StorageClass.Stemming]: "Stemming",
};

export const storageClassFlag = (storageClass: StorageClass) => 1 << storageClass;

export enum StockLocationKind {
  DetonatorMagazine = 0,
  ExplosivesMagazine = 1,
  BulkPrecursorStore = 2,
  Mmu = 3,
  Quarantine = 4,
}

export const STORE_KIND_LABELS: Record<StockLocationKind, string> = {
  [StockLocationKind.DetonatorMagazine]: "Detonator magazine",
  [StockLocationKind.ExplosivesMagazine]: "Explosives magazine",
  [StockLocationKind.BulkPrecursorStore]: "Bulk precursor store",
  [StockLocationKind.Mmu]: "MMU",
  [StockLocationKind.Quarantine]: "Quarantine",
};

export enum StockOwnership {
  MineOwned = 0,
  SupplierConsignment = 1,
}

export enum LicenceKind {
  Shotfirer = 0,
  DpimBlastController = 1,
  MagazineKeeper = 2,
  ExplosivesTransport = 3,
  DrillOperator = 4,
}

export const LICENCE_LABELS: Record<LicenceKind, string> = {
  [LicenceKind.Shotfirer]: "Shotfirer",
  [LicenceKind.DpimBlastController]: "Blast controller (DPIM)",
  [LicenceKind.MagazineKeeper]: "Magazine keeper",
  [LicenceKind.ExplosivesTransport]: "Explosives transport",
  [LicenceKind.DrillOperator]: "Drill operator",
};

export enum ExplosiveTransactionKind {
  Receipt = 0,
  IssueToBlast = 1,
  ReturnFromBlast = 2,
  Transfer = 3,
  Destruction = 4,
  Adjustment = 5,
  Stocktake = 6,
  Reversal = 7,
}

export const MOVEMENT_LABELS: Record<ExplosiveTransactionKind, string> = {
  [ExplosiveTransactionKind.Receipt]: "Receipt",
  [ExplosiveTransactionKind.IssueToBlast]: "Issue to blast",
  [ExplosiveTransactionKind.ReturnFromBlast]: "Return from blast",
  [ExplosiveTransactionKind.Transfer]: "Transfer",
  [ExplosiveTransactionKind.Destruction]: "Destruction",
  [ExplosiveTransactionKind.Adjustment]: "Adjustment",
  [ExplosiveTransactionKind.Stocktake]: "Stocktake",
  [ExplosiveTransactionKind.Reversal]: "Reversal",
};

export enum LedgerStatus {
  Posted = 0,
  Parked = 1,
  Discarded = 2,
}

export interface PersonnelLicenceDTO {
  id: number;
  personnelId: number;
  kind: LicenceKind;
  number: string;
  issuedBy?: string | null;
  issuedOn?: DateTime | null;
  expiresOn?: DateTime | null;
  rowVersion?: string | null;
}

export interface PersonnelDTO {
  id: number;
  name: string;
  employeeNo?: string | null;
  employer?: string | null;
  zoneId?: number | null;
  zoneName?: string | null;
  isActive: boolean;
  canEdit: boolean;
  rowVersion?: string | null;
  licences: PersonnelLicenceDTO[];
}

export interface SavePersonnelRequest {
  name: string;
  employeeNo?: string | null;
  employer?: string | null;
  zoneId?: number | null;
  isActive: boolean;
  rowVersion?: string | null;
}

export interface SaveLicenceRequest {
  kind: LicenceKind;
  number: string;
  issuedBy?: string | null;
  issuedOn?: string | null;
  expiresOn?: string | null;
  rowVersion?: string | null;
}

export interface PersonOptionDTO {
  id: number;
  name: string;
  employer?: string | null;
  currentLicences: LicenceKind[];
}

export interface MagazineDTO {
  id: number;
  name: string;
  kind: StockLocationKind;
  ownerZoneId: number;
  ownerZoneName?: string | null;
  ownership: StockOwnership;
  locationId?: number | null;
  locationName?: string | null;
  plantId?: number | null;
  plantRef?: string | null;
  licenceNumber?: string | null;
  licensedNeqKg?: number | null;
  licenceExpiresOn?: DateTime | null;
  allowedStorageClasses: number;
  isActive: boolean;
  canTransact: boolean;
  rowVersion?: string | null;
}

export interface SaveMagazineRequest {
  name: string;
  kind: StockLocationKind;
  ownerZoneId: number;
  ownership: StockOwnership;
  locationId?: number | null;
  plantId?: number | null;
  licenceNumber?: string | null;
  licensedNeqKg?: number | null;
  licenceExpiresOn?: string | null;
  allowedStorageClasses: number;
  isActive: boolean;
  rowVersion?: string | null;
}

export interface StockBalanceDTO {
  locationId: number;
  blastMaterialId: number;
  material: string;
  unit: MaterialUnit;
  lotId?: number | null;
  lotCode?: string | null;
  expiresOn?: DateTime | null;
  quantity: number;
  neqKg: number;
}

export interface MagazineStockDTO {
  magazine: MagazineDTO;
  neqKg: number;
  hasPendingStocktake: boolean;
  parkedMovements: number;
  balances: StockBalanceDTO[];
}

export interface ExplosiveMaterialOptionDTO {
  id: number;
  name: string;
  materialType: string;
  unit: MaterialUnit;
  storageClass: StorageClass;
  neqKgPerUnit: number;
  packSize?: number | null;
  isLotTracked: boolean;
  isBulk: boolean;
}

export interface ExplosiveLineRequest {
  blastMaterialId: number;
  lotId?: number | null;
  lotCode?: string | null;
  lotExpiresOn?: string | null;
  fromLocationId?: number | null;
  toLocationId?: number | null;
  quantity: number;
}

export interface PostExplosiveTransactionRequest {
  kind: ExplosiveTransactionKind;
  occurredAt: DateTime;
  patternBlastId?: number | null;
  issuedToPersonId?: number | null;
  witnessPersonId?: number | null;
  reference?: string | null;
  reason?: string | null;
  notes?: string | null;
  lines: ExplosiveLineRequest[];
  acknowledge: boolean;
  clientMutationId?: string | null;
}

export interface ExplosiveTransactionLineDTO {
  id: number;
  blastMaterialId: number;
  material: string;
  unit: MaterialUnit;
  lotId?: number | null;
  lotCode?: string | null;
  fromLocationId?: number | null;
  fromLocation?: string | null;
  toLocationId?: number | null;
  toLocation?: string | null;
  quantity: number;
}

export interface ExplosiveTransactionDTO {
  id: number;
  kind: ExplosiveTransactionKind;
  status: LedgerStatus;
  occurredAt: DateTime;
  enteredAt: DateTime;
  enteredBy?: string | null;
  isLate: boolean;
  patternBlastId?: number | null;
  patternReference?: string | null;
  issuedToPersonId?: number | null;
  issuedToPerson?: string | null;
  witnessPersonId?: number | null;
  witnessPerson?: string | null;
  reference?: string | null;
  reason?: string | null;
  notes?: string | null;
  parkedReason?: string | null;
  resolvedAt?: DateTime | null;
  resolvedBy?: string | null;
  resolution?: string | null;
  reversesTransactionId?: number | null;
  reversedByTransactionId?: number | null;
  lines: ExplosiveTransactionLineDTO[];
}

export interface ExplosivePostResultDTO {
  posted: boolean;
  warnings: string[];
  transaction?: ExplosiveTransactionDTO | null;
}

export interface MaterialRegisterDetailsDTO {
  unit: MaterialUnit;
  neqKgPerUnit: number;
  unNumber?: string | null;
  hazardDivision?: string | null;
  storageClass: StorageClass;
  isBulk: boolean;
  isLotTracked: boolean;
  shelfLifeMonths?: number | null;
  packSize?: number | null;
  productCode?: string | null;
  supplier?: string | null;
  standardCost?: number | null;
}

export enum StocktakeStatus {
  Counting = 0,
  PendingApproval = 1,
  Posted = 2,
  Cancelled = 3,
}

export interface StocktakeSheetLineDTO {
  blastMaterialId: number;
  material: string;
  unit: MaterialUnit;
  packSize?: number | null;
  lotId?: number | null;
  lotCode?: string | null;
}

export interface StocktakeCountRequest {
  blastMaterialId: number;
  lotId?: number | null;
  lotCode?: string | null;
  packs?: number | null;
  loose?: number | null;
}

export interface SubmitStocktakeRequest {
  magazineId: number;
  countedAt: DateTime;
  sheetReference?: string | null;
  counts: StocktakeCountRequest[];
}

export interface StocktakeSubmittedDTO {
  stocktakeId: number;
  status: StocktakeStatus;
}

export interface StocktakeCountDTO {
  blastMaterialId: number;
  material: string;
  unit: MaterialUnit;
  lotId?: number | null;
  lotCode?: string | null;
  bookQuantity: number;
  countedQuantity: number;
  variance: number;
}

export interface PendingStocktakeDTO {
  id: number;
  magazineId: number;
  magazine: string;
  countedAt: DateTime;
  countedBy?: string | null;
  sheetReference?: string | null;
  canApprove: boolean;
  rowVersion?: string | null;
  counts: StocktakeCountDTO[];
}

export interface ReconciliationSignOffDTO {
  reason: string;
  signedBy?: string | null;
  signedAt: DateTime;
}

export interface ReconciliationLineDTO {
  blastMaterialId: number;
  material: string;
  unit: MaterialUnit;
  issued: number;
  returned: number;
  destroyed: number;
  charged: number;
  variance: number;
  withinTolerance: boolean;
  needsSignOff: boolean;
  canSignOff: boolean;
  signOff?: ReconciliationSignOffDTO | null;
}

export interface DeliveredDownTheHoleDTO {
  product: string;
  supplier?: string | null;
  unit: MaterialUnit;
  quantity: number;
  value?: number | null;
  blasts: string[];
}
